import { createContext, useContext, useMemo } from 'react';

import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridColDef,
  GridValidRowModel,
} from '@mui/x-data-grid-premium';

import { GeneratikTableColumn } from '../GeneratikTable.types';
import { useGeneratikTableContext } from '../GeneratikTableContext';

const SelectionCheckboxRowContext = createContext<GridValidRowModel | undefined>(undefined);

/**
 * The row whose selection checkbox is currently rendering — `undefined` for the
 * select-all checkbox in the column header, which belongs to no row.
 */
export const useSelectionCheckboxRow = () => useContext(SelectionCheckboxRowContext);

// MUI renders the `baseCheckbox` slot with no row information at all, so a per-row tooltip
// can only reach it sideways: the selection cell publishes its row and the slot reads it back.
const renderSelectionCheckboxCell: GridColDef['renderCell'] = (params) => (
  <SelectionCheckboxRowContext.Provider value={params.row}>
    {GRID_CHECKBOX_SELECTION_COL_DEF.renderCell?.(params)}
  </SelectionCheckboxRowContext.Provider>
);

const rowScopedSelectionColumn: GeneratikTableColumn = {
  ...GRID_CHECKBOX_SELECTION_COL_DEF,
  renderCell: renderSelectionCheckboxCell,
};

/**
 * Columns as the grid should render them. Only tables asking for a per-row disabled tooltip
 * pay for the row-scoped selection column; everyone else keeps MUI's own, untouched.
 * The grid drops the column again when selection is off, so no guard is needed here.
 */
const useSelectionCheckboxColumns = (): GeneratikTableColumn[] => {
  const { processedColumns, disabledCheckboxTooltip } = useGeneratikTableContext();

  return useMemo(
    () =>
      typeof disabledCheckboxTooltip === 'function'
        ? [rowScopedSelectionColumn, ...processedColumns]
        : processedColumns,
    [disabledCheckboxTooltip, processedColumns],
  );
};

export default useSelectionCheckboxColumns;
